import React, { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Dialog, DialogTitle, Divider, Grid } from '@mui/material';
import {
  BooleanInput,
  Button,
  CreateBase,
  FormDataConsumer,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import z from 'zod';
import { noop } from 'lodash';

import client from 'src/data/api';
import { CustomerDataPackageRecord } from 'src/types';
import { zodIssuesIntoErrorsMap } from 'src/utils/validation';
import CraftTwoColumns from 'src/components/CraftTwoColumns';

interface AddCompanySubscriptionsDialogContextValue {
  open: boolean;
  setOpen: () => void;
  setClosed: () => void;
  onSuccess?: (() => void) | undefined;
  setOnSuccess: (fn: () => void) => void;
}

const addCompanySubscriptionsDialogContextDefaultValue = {
  open: false,
  setOpen: noop,
  setClosed: noop,
  setOnSuccess: noop,
};

const AddCompanySubscriptionsDialogContext = createContext<AddCompanySubscriptionsDialogContextValue>(
  addCompanySubscriptionsDialogContextDefaultValue,
);

export const AddCompanySubscriptionsDialogContextProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [onSuccess, setOnSuccess] = useState<() => void>();

  const value = useMemo(
    () => ({
      open,
      setOpen: () => setOpen(true),
      setClosed: () => setOpen(false),
      onSuccess,
      setOnSuccess: (fn: () => void) => setOnSuccess(() => fn),
    }),
    [open, setOpen],
  );

  return (
    <AddCompanySubscriptionsDialogContext.Provider value={value}>
      {children}
    </AddCompanySubscriptionsDialogContext.Provider>
  );
};

export const useAddCompanySubscriptionsDialogContext = () => useContext(AddCompanySubscriptionsDialogContext);

const CustomToolbar = ({ onCancel }: { onCancel: () => void }) => (
  <Toolbar>
    <Grid container justifyContent="space-between">
      <Button label="Cancel" onClick={onCancel} />
      <SaveButton />
    </Grid>
  </Toolbar>
);

const companySubscriptionsSchema = z.object({
  allCompanies: z.boolean(),
  companyIds: z
    .string()
    .nonempty()
    .trim()
    .regex(/^[\d\n]+$/, 'Company IDs should only contain IDs, each on a separate line')
    .transform((val) => val.split(/\n+/))
    .optional(),
});

export const AddCompanySubscriptionsDialog = () => {
  const dialogCtx = useAddCompanySubscriptionsDialogContext();
  const customerDataPackageRecord = useRecordContext<CustomerDataPackageRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [createSubscriptions] = useMutation<{
    insert_subscriptions: { returning: { id: string }[] };
  }>(
    gql`
      mutation insert_subscriptions($objects: [subscriptions_insert_input!]!) {
        insert_subscriptions(
          objects: $objects
          on_conflict: { constraint: subscriptions_ci_cdpi_unique, update_columns: [] }
        ) {
          returning {
            id
          }
        }
      }
    `,
    { client, errorPolicy: 'all' },
  );

  const [createAllCompaniesSubscription] = useMutation(
    gql`
      mutation createAllCompaniesSubscription($externalApiClientID: Int!, $customerDataPackageID: Int!) {
        delete_subscriptions(where: { customer_data_package_id: { _eq: $customerDataPackageID } }) {
          affected_rows
        }
        insert_subscriptions(
          objects: [
            {
              external_api_client_id: $externalApiClientID
              customer_data_package_id: $customerDataPackageID
              all_companies: true
            }
          ]
        ) {
          returning {
            id
          }
        }
      }
    `,
    { client, errorPolicy: 'all' },
  );

  const handleSubmit = async (rawValues: Record<string, unknown>) => {
    if (!customerDataPackageRecord) return undefined;

    const validationResult = companySubscriptionsSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);
    const values = validationResult.data;

    const subscriptionsToInsert = values.companyIds?.map((companyID) => ({
      external_api_client_id: customerDataPackageRecord.external_api_client_id,
      company_id: companyID,
      customer_data_package_id: customerDataPackageRecord.id,
    }));

    const { errors } = await (values.allCompanies
      ? createAllCompaniesSubscription({
          variables: {
            externalApiClientID: customerDataPackageRecord.external_api_client_id,
            customerDataPackageID: customerDataPackageRecord.id,
          },
        })
      : createSubscriptions({ variables: { objects: subscriptionsToInsert } }));

    if (errors) {
      notify('There was a problem adding the subscriptions. Please try again.', { type: 'error' });
      return undefined;
    }

    refresh();
    notify('Subscriptions added successfully.', { type: 'success' });
    if (dialogCtx.onSuccess) dialogCtx.onSuccess();
    dialogCtx.setClosed();
    return undefined;
  };

  return (
    <Dialog open={dialogCtx.open} onClose={dialogCtx.setClosed}>
      <DialogTitle>Add company subscriptions</DialogTitle>
      <Divider />

      <CreateBase>
        <SimpleForm onSubmit={handleSubmit} toolbar={<CustomToolbar onCancel={dialogCtx.setClosed} />} mt={2} mx={1}>
          <FormDataConsumer>
            {({ formData }) => (
              <CraftTwoColumns
                left={<BooleanInput label="All Companies" source="allCompanies" />}
                right={
                  <TextInput
                    label="Company ID(s)"
                    source="companyIds"
                    multiline
                    disabled={formData.allCompanies}
                    validate={formData.allCompanies ? [] : required()}
                    fullWidth
                    minRows={15}
                    maxRows={15}
                    autoFocus
                  />
                }
              />
            )}
          </FormDataConsumer>
        </SimpleForm>
      </CreateBase>
    </Dialog>
  );
};
